/**
 * Lo splash **nativo**: quello che il sistema operativo disegna prima che la WebView esista.
 *
 * ## Perché serve un modulo e non una chiamata
 *
 * ⚠️ Questo modulo chiude la schermata del **plugin**, non l'animazione che si vede muoversi:
 * quella e' `shared/components/SplashAnimato.tsx`, il logo in 3D, ed e' proprio lui a chiamare
 * questa funzione — sotto la vista ferma c'e' gia' lo stesso marchio, quindi va tolta subito.
 *
 * Con `AutoHideSplashScreen = false` lo splash non si chiude da solo: lo chiude l'applicazione quando è
 * pronta. È ciò che permette di sostituirlo con il logo animato senza uno sfarfallio in mezzo — ma
 * sposta su di noi una responsabilità che prima aveva il sistema: **se non lo chiudiamo, l'app resta
 * bloccata su un'immagine.** Per sempre, senza errori, senza modo per l'utente di uscirne.
 *
 * È il difetto classico di questa configurazione, e qui è disinnescato in due modi:
 *
 * 1. `nascondiSplashNativo()` non solleva mai e si può chiamare quante volte si vuole;
 * 2. `assicuraChiusuraSplash()` arma un tetto di tempo all'avvio: passato quello, lo splash va giù
 *    comunque — che il logo sia partito, fallito, o non sia mai stato montato.
 *
 * Il secondo non è una cintura sulle bretelle: copre i casi in cui il codice che *dovrebbe* chiudere
 * lo splash non gira affatto — un errore durante il mount di React, un chunk che non arriva, un
 * dispositivo senza WebGL su cui il componente del logo non monta niente.
 */
import { nativeRuntime } from '@/shared/lib/nativePlatform';

/**
 * Oltre questo tempo dall'avvio lo splash si chiude comunque.
 *
 * Quattro secondi: abbastanza perché l'animazione del logo si veda tutta su un telefono lento,
 * poco perché nessuno pensi che l'app sia bloccata.
 */
export const SPLASH_CAP_MS = 4_000;

let alreadyClosed = false;

/**
 * Chiude lo splash nativo. Idempotente e silenziosa.
 *
 * Silenziosa di proposito: qui un errore non ha rimedio da proporre all'utente — lo splash o si
 * chiude o si chiude col tetto di tempo — e un avviso su una schermata d'avvio è rumore.
 */
export async function hideNativeSplash(): Promise<void> {
  const runtime = nativeRuntime();
  if (runtime === 'web' || alreadyClosed) return;
  alreadyClosed = true;

  /*
   * ⚠️ Questa chiamata **serve davvero**, e non e' un allineamento formale: il `config.xml` del
   * guscio dichiara `AutoHideSplashScreen = false`, e il `SplashScreenPlugin.java` di
   * cordova-android accetta l'azione `hide` **solo** in quel caso — cioe' con quella
   * configurazione lo splash lo deve chiudere l'app. Senza questa chiamata resta su fino al
   * tetto di `SplashScreenDelay` (3 secondi), e un'applicazione che mostra la schermata
   * d'avvio per tre secondi dopo essere pronta sembra lenta senza esserlo.
   *
   * ⚠️ `navigator.splashscreen` non viene da un plugin installato: e' **incorporato** in
   * cordova-android (`templates/project/assets/www/cordova.js` lo lega a
   * `CordovaSplashScreenPlugin`). Per questo fra i plugin del guscio non ne compare nessuno
   * per lo splash, e cercarlo li' fa concludere che manchi.
   */
  try {
    const ss = (window as { navigator?: { splashscreen?: { hide?: () => void } } }).navigator?.splashscreen;
    ss?.hide?.();
  } catch { /* come sopra: su una schermata d'avvio un avviso e' rumore */ }
}

/** Arma il tetto di tempo. Da chiamare una volta all'avvio, prima di montare l'app. */
export function ensureSplashClosing(cap = SPLASH_CAP_MS): void {
  if (nativeRuntime() === 'web') return;
  setTimeout(() => { void hideNativeSplash(); }, cap);
}

/** Solo per i test: rimette lo stato a «splash ancora aperto». */
export function resetSplash(): void {
  alreadyClosed = false;
}
